import React, { useEffect, useRef, useState } from 'react';

import { CSVLink } from 'react-csv';
// import ReactExport from 'react-export-excel';
import { useReactToPrint } from 'react-to-print';
// import { PDFDownloadLink } from '@react-pdf/renderer';

import { IconCopy01, IconPrinter } from '@pentabd/icons';

import Text from '../text/Text';
import { Button } from '../button';
import copyToClipboard from './components/copy';
import { filterProperties } from './components/helpers';
import { Column, Row } from '../table/component/types';
// import PdfDoc from './components/PdfDoc';

enum FILE {
  CSV = 'csv',
  PDF = 'pdf',
  COPY = 'copy',
  PRINT = 'print',
}
interface DownloadButtonItemProps {
  name: string;
  title?: string;
}
export interface DownloadButtonsProps<T extends Row> {
  fileName: string;
  columns?: Column<T>[];
  rows?: Row[];
  isLandscapeOrientation?: boolean;
  onClickDownload?: () => void;
  downloadLoading?: boolean;
  buttons?: DownloadButtonItemProps[];
  buttonSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

// for Excel file generation
// const ExcelFile = ReactExport.ExcelFile;
// const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
// const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

// const excelColumnGenerator = (columns: any) => {
//   return columns.map((item, index: number) => (
//     <ExcelColumn key={index} label={item.name} value={item.key} />
//   ));
// };

function DownloadButtons<T extends Row>({
  fileName,
  columns = [],
  rows = [],
  isLandscapeOrientation = false,
  onClickDownload,
  downloadLoading = false,
  buttonSize = 'sm',
  buttons = [{ name: FILE.COPY }, { name: FILE.CSV }, { name: FILE.PRINT }],
}: DownloadButtonsProps<T>) {
  const [currentButton, setCurrentButton] = useState('');
  const [filteredRows, setFilteredRows] = useState<any[]>([]);

  const csvLink = useRef<any>();
  const pdfLink = useRef<any>();
  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    documentTitle: fileName,
    content: () => componentRef.current,
  });

  useEffect(() => {
    const tableKeys = columns
      .filter((col) => !col?.hide)
      .map((item) => item?.key);

    const filteredData = rows.map((obj) => filterProperties(obj, tableKeys));

    setFilteredRows(filteredData);
  }, [columns, rows]);

  const btnClickHandler = (btnName: string) => {
    setCurrentButton(btnName);
    onClickDownload();
  };

  const csvHandler = () => {
    if (csvLink?.current?.link?.click) {
      csvLink.current.link.click();
      setCurrentButton('');
    }
  };

  const copyFileHandler = () => {
    copyToClipboard(filteredRows);
    setCurrentButton('');
  };

  const printFileHandler = () => {
    handlePrint();
    setCurrentButton('');
  };

  const pdfHandler = () => {
    if (pdfLink?.current?.click) {
      pdfLink.current.click();
      setCurrentButton('');
    }
  };

  const delayedFn = () => {
    if (currentButton === FILE.CSV) csvHandler();
    else if (currentButton === FILE.COPY) copyFileHandler();
    else if (currentButton === FILE.PRINT) printFileHandler();
    else if (currentButton === FILE.PDF) pdfHandler();
  };

  function findButtonTitle(name: string, staticTitle: string) {
    const buttonTitle =
      buttons.find((item) => item.name === name).title || staticTitle;
    return buttonTitle;
  }
  useEffect(() => {
    if (!downloadLoading && currentButton !== '') {
      setTimeout(delayedFn, 1000);
    }
  }, [currentButton, downloadLoading]);

  return (
    <div className="d-flex gap-6">
      {/* copy btn */}
      {buttons?.find(
        (item: DownloadButtonItemProps) => item?.name === FILE.COPY,
      ) ? (
        <>
          <Button
            size={buttonSize}
            fill="transparent"
            className="border"
            type="light"
            onClick={() => btnClickHandler(FILE.COPY)}
            loading={currentButton === FILE.COPY && downloadLoading}
          >
            <IconCopy01 size="14" fill="fill-subtitle2" />
            <Text size={buttonSize} weight="semibold">
              {findButtonTitle(FILE.COPY, 'Copy')}
            </Text>
          </Button>
        </>
      ) : null}

      {/* csv btn */}
      {buttons?.find(
        (item: DownloadButtonItemProps) => item?.name === FILE.CSV,
      ) ? (
        <>
          <Button
            size={buttonSize}
            fill="transparent"
            className="border"
            type="light"
            loading={currentButton === FILE.CSV && downloadLoading}
            onClick={() => btnClickHandler(FILE.CSV)}
          >
            <Text size={buttonSize} weight="semibold">
              {findButtonTitle(FILE.CSV, 'CSV')}
            </Text>
          </Button>
          <CSVLink
            data={[...filteredRows]}
            filename={`${fileName}.csv`}
            style={{
              display: 'none',
            }}
            ref={csvLink}
          />
        </>
      ) : null}

      {/* <ExcelFile
        element={
          <Button size={buttonSize} fill="transparent" className="border" type="light">
            <IconExcel size="20" />
            <Text size={buttonSize} weight="semibold">
              Excel
            </Text>
          </Button>
        }
        filename={fileName}
      >
        <ExcelSheet data={[...filteredRows]} name={fileName}>
          {excelColumnGenerator(columns)}
        </ExcelSheet>
      </ExcelFile> */}

      {/* print - btn */}
      {buttons?.find(
        (item: DownloadButtonItemProps) => item?.name === FILE.PRINT,
      ) ? (
        <>
          {/* for print, only displayed in the print ---> starts here */}
          <div className="d-none">
            <table ref={componentRef} border={1} className="border-collapse">
              <thead>
                <tr>
                  {columns?.map((column, index) =>
                    !column?.hide ? <th key={index}>{column.name}</th> : null,
                  )}
                </tr>
              </thead>
              <tbody>
                {rows &&
                  rows?.map((rowData, rowIndex) => (
                    <tr key={rowIndex}>
                      {columns?.map((column, columnIndex) =>
                        !column?.hide ? (
                          <td key={columnIndex}>{`${
                            rowData?.[column?.key] ?? ''
                          }`}</td>
                        ) : null,
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>

            {/* for landscape print style */}
            {isLandscapeOrientation && (
              <style type="text/css" media="print">
                {`@page { size: landscape; }`}
              </style>
            )}
          </div>
          {/* for print, only displayed in the print ---> ends here */}

          <Button
            size={buttonSize}
            fill="transparent"
            className="border"
            type="light"
            // onClick={handlerPrinting}
            onClick={() => btnClickHandler(FILE.PRINT)}
            loading={currentButton === FILE.PRINT && downloadLoading}
          >
            <IconPrinter size="14" fill="fill-subtitle2" />
            <Text size={buttonSize} weight="semibold">
              {findButtonTitle(FILE.PRINT, 'Print')}
            </Text>
          </Button>
        </>
      ) : null}

      {/* <PDFDownloadLink
        document={
          <PdfDoc
            columns={columns}
            rows={filteredRows}
            fileName={{ fileName }}
          />
        }
        fileName={fileName}
      >
        <Button size={buttonSize} fill="transparent" className="border" type="light">
          <IconFile04 size="14" fill="fill-subtitle2" />
          <Text size={buttonSize} weight="semibold">
            PDF
          </Text>
        </Button>
      </PDFDownloadLink> */}
    </div>
  );
}

export default DownloadButtons;
